import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, Calendar, Car, Euro } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const TOKEN_KEY = "elecars_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { token: input });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Acceso concedido");
    } catch {
      toast.error("Token inválido");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  };

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/leads", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeads(data);
      } catch {
        toast.error("Sesión expirada");
        logout();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="w-full max-w-md bg-white/[0.02] border border-white/10 p-8 sm:p-10"
          data-testid="admin-login-form"
        >
          <div className="flex flex-col items-center gap-6 mb-8">
            <Logo size="md" />
            <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
          </div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">
            Token de administrador
          </label>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="admin-token-input"
            className="w-full bg-transparent border border-white/15 px-4 py-3.5 text-[#FFFFF0] text-sm"
            placeholder="••••••••••••"
            required
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 w-full bg-[#D4AF37] text-black px-6 py-3.5 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#E5C158] transition-colors disabled:opacity-60"
          >
            {loading ? "Verificando..." : "Acceder"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-10 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Cerrar sesión
          </button>
        </div>

        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]">Panel admin</span>
          <h1 className="font-heading mt-4 text-3xl sm:text-4xl font-light tracking-tight">
            Leads recibidos
          </h1>
          <p className="mt-2 text-sm text-[#A3A3A3]">
            Total: <span className="text-[#FFFFF0] font-medium">{leads.length}</span>
          </p>
        </div>

        {loading && <p className="text-[#A3A3A3]">Cargando...</p>}

        {!loading && leads.length === 0 && (
          <div className="border border-white/10 p-10 text-center text-[#A3A3A3]">
            Aún no hay leads registrados.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {leads.map((l, i) => (
            <div
              key={l.id}
              data-testid={`admin-lead-${i}`}
              className="bg-white/[0.02] border border-white/10 p-6 hover:border-[#D4AF37]/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg font-medium text-[#FFFFF0]">{l.name}</h3>
                  <p className="text-xs text-[#737373] mt-1 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {new Date(l.created_at).toLocaleString("es-ES")}
                  </p>
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-2 py-1">
                  Nuevo
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <Row icon={Mail} label={l.email} />
                <Row icon={Phone} label={l.phone} />
                <Row icon={Car} label={l.vehicle_type} />
                <Row icon={Euro} label={l.budget} />
              </div>

              {l.message && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-2">Mensaje</p>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed font-light">{l.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
    <span className="text-[#FFFFF0]">{label}</span>
  </div>
);
